import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Link,
  useNavigate,
  useParams,
  useLocation,
  useSearchParams
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import LayoutTabs from "@/components/controls/LayoutTabs";
import IconContainer from "@/components/ui/icon-container";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import SingleInput from "@/components/controls/SingleInput";
import {
  Check,
  ChevronRight,
  Contracts as ContractsIcon,
  EmptyStateIllustrationRetentionWorkflow,
  Onboarding,
  Tasks,
  Workflow
} from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import TasksDrawer from "@/components/layouts/TasksDrawer/TasksDrawer";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import {
  useContract,
  useUpdateContract,
  useUpdateDealCurrency,
  useDeleteContract,
  ContractUpdatePayload
} from "@/pages/contracts/hooks";
import { useCopyLink } from "@/services/hooks/useCopyLink";
import DeleteEntityModal from "@/components/layouts/DeleteEntityModal";
import {
  ContractData,
  ContractTab,
  ContractUploadFiles,
  ENABLE_ONBOARDING_PHASE_KEY,
  isContractTab
} from "@/pages/contracts/types";
import { ComputedFields } from "@/services/api/types";
import {
  coerceContractFieldValue,
  coerceContractFields,
  ContractFieldValueOptions
} from "@/pages/contracts/contractFieldValue";
import ContractTopFields from "./ContractTopFields";
import ContractOverview from "./ContractOverview";
import ContractTermsAndServicesTab from "./ContractTermsAndServicesTab";
import ContractPeopleTab from "./ContractPeopleTab";
import Activities from "@/components/layouts/Activities/Activities";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useHasAdditionalFields } from "@/pages/settings/hooks";
import { TAB_SEARCH_PARAM } from "@/components/layouts/DetailedView";
import InTheMakingTab from "./InTheMakingTab";
import { useAccountSettingByKey } from "@/entities/settings/hooks";
import { useBooleanAccountSetting } from "@/entities/settings/useBooleanAccountSetting";
import OnboardingBadge from "@/pages/contracts/ContractDetailedView/onboardingTab/components/OnboardingBadge";
import OnboardingStageSelection from "@/pages/contracts/ContractDetailedView/onboardingTab/components/OnboardingStageSelection";
import OnboardingGuardrailsModal from "@/pages/contracts/ContractDetailedView/onboardingTab/components/OnboardingGuardrailsModal";
import OnboardingTab, {
  useContractOnboarding,
  buildOnboardingTopOptions
} from "@/pages/contracts/ContractDetailedView/onboardingTab/OnboardingTab";
import { Button } from "@/components/ui/button";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { simpleDate } from "@/utils/datetime";
import { cn } from "@/utils/utils";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import AdditionalInfoTab from "@/components/layouts/AdditionalInfoTab";
import {
  keysOwnedBy,
  keysToConfirm,
  applyFields,
  FieldWriteIds
} from "./fieldWriteOwnership";

export type ContractFieldUpdate = (
  field: string,
  newValue: string | number | boolean | string[] | ContractUploadFiles | null,
  additionalFields?: Record<string, string | number | boolean | null>,
  options?: ContractFieldValueOptions
) => void;

const ContractDetailedView: React.FC = () => {
  const { t } = useTranslation();
  const { contractId } = useParams<{ contractId: string }>();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get(TAB_SEARCH_PARAM);
  const {
    data: fetchedContract,
    isLoading,
    isError,
    refetch: refetchContract
  } = useContract(contractId, { computed_fields: ComputedFields.ALL });
  // `undefined` until the setting loads, so the onboarding tab, badge and
  // actions never show and then disappear for an account that turned it off.
  const onboardingEnabled =
    useBooleanAccountSetting(ENABLE_ONBOARDING_PHASE_KEY).value === true;
  const {
    data: additionalInfoTabSetting,
    isLoading: isLoadingAdditionalInfoTabSetting
  } = useAccountSettingByKey("additional_info_tab_enabled");
  const hasAdditionalFields = useHasAdditionalFields(EntityType.CONTRACT);
  const showAdditionalInfoTab =
    additionalInfoTabSetting?.value === true && hasAdditionalFields;
  const [contract, setContract] = useState<ContractData | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<ContractTab>(
    isContractTab(urlTab) && urlTab !== ContractTab.ADDITIONAL_INFO
      ? urlTab
      : ContractTab.OVERVIEW
  );
  const [isTasksDrawerOpen, setIsTasksDrawerOpen] = useState(false);
  const toggleTasksDrawer = () => setIsTasksDrawerOpen((prev) => !prev);

  const updateContract = useUpdateContract(contract?.id);
  const updateDealCurrency = useUpdateDealCurrency(contract?.id);
  const deleteContract = useDeleteContract();
  const handleCopyLink = useCopyLink();
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { enabled: showDuplicateContract } = useFeatureFlag(
    FeatureFlags.CRM_7713_DUPLICATE_CONTRACT
  );

  const changeTab = useCallback(
    (tab: ContractTab) => {
      setActiveTab(tab);
      const params = new URLSearchParams(location.search);
      params.set(TAB_SEARCH_PARAM, tab);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  useEffect(() => {
    if (!isContractTab(urlTab) || urlTab === activeTab) return;
    if (urlTab === ContractTab.ADDITIONAL_INFO) {
      if (isLoadingAdditionalInfoTabSetting) return;
      if (!showAdditionalInfoTab) {
        changeTab(ContractTab.OVERVIEW);
        return;
      }
    }
    setActiveTab(urlTab);
  }, [
    urlTab,
    activeTab,
    isLoadingAdditionalInfoTabSetting,
    showAdditionalInfoTab,
    changeTab
  ]);

  // Two edits made close together race: an earlier PUT can resolve after a
  // later one, carrying a pre-edit snapshot that silently undoes it.
  // latestWriteIdRef drops responses answering anything but the newest write;
  // pendingWriteCountRef extends the same guard to the refetch sync below.
  const latestWriteIdRef = useRef(0);
  const pendingWriteCountRef = useRef(0);
  // Which write last touched each field. A failed write may only revert a key
  // it still owns: reverting one a newer write has since claimed would discard
  // that newer value, including one the server already confirmed.
  const fieldWriteIdsRef = useRef<FieldWriteIds>({});
  // The newest contract the server has actually confirmed. `contract` itself
  // carries optimistic values, so it is the wrong thing to restore from: when
  // two writes to one field both fail, the second would otherwise revert to the
  // first one's unconfirmed guess and leave a value the server never accepted.
  const confirmedContractRef = useRef<ContractData | undefined>(undefined);
  // Which write's response last advanced the confirmed baseline for each field.
  // Separate from fieldWriteIdsRef, which records which write last *dispatched*
  // for a field: a write can lose ownership to a newer one and still be the
  // newest thing the server has confirmed.
  const confirmedWriteIdsRef = useRef<FieldWriteIds>({});

  // Set when a server snapshot arrives while a write is still in flight, so it
  // has to be dropped. Neither handler refetches on its own, so without this the
  // fields the server computes for us — ARR, MRR, status — could keep a value
  // from before that write, with nothing scheduled to correct them.
  const droppedFetchRef = useRef(false);

  useEffect(() => {
    if (!fetchedContract) return;
    if (pendingWriteCountRef.current !== 0) {
      droppedFetchRef.current = true;
      return;
    }
    confirmedContractRef.current = fetchedContract;
    // A fresh server snapshot supersedes every per-field confirmation.
    confirmedWriteIdsRef.current = {};
    setContract(fetchedContract);
  }, [fetchedContract]);

  // Called from both write handlers once their count is decremented: the last
  // write to settle re-fetches if any snapshot was dropped while it was in
  // flight. Applying the dropped snapshot instead would paint data older than
  // the write that displaced it.
  const refetchIfSnapshotWasDropped = useCallback(() => {
    if (pendingWriteCountRef.current !== 0) return;
    if (!droppedFetchRef.current) return;
    droppedFetchRef.current = false;
    void refetchContract();
  }, [refetchContract]);

  // Invalidate contracts table cache on unmount so the table reflects
  // derived fields (ARR, MRR, status) updated via the revenue-item write path.
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({
        queryKey: ["contracts/filter"]
      });
    };
  }, [queryClient]);

  const onUpdateField: ContractFieldUpdate = useCallback(
    (field, newValue, additionalFields, options) => {
      if (!contract) return;
      const propagatesCurrencyToDeal =
        field === "currency" &&
        !!contract.dealId &&
        !contract.isDetachedFromDeal &&
        typeof newValue === "string";
      if (propagatesCurrencyToDeal) {
        updateDealCurrency.mutate({
          pathParams: { dealId: contract.dealId },
          data: { currency: newValue }
        });
      }
      const finalValue = coerceContractFieldValue(field, newValue, options);
      const normalizedAdditionalFields = coerceContractFields(
        additionalFields,
        options
      );
      const updated = {
        ...contract,
        [field]: finalValue,
        ...normalizedAdditionalFields
      };
      const previous = contract;
      setContract(updated);

      // onboardingStageId is managed by the dedicated stage-move endpoint which
      // enforces guardrails. Only update local state here; no backend PUT needed.
      if (field === "onboardingStageId") return;

      // dealId is a financial field on the server: including it on every PUT
      // makes non-financial edits fail the financials-permission check on a
      // locked contract. Send it only where it does work — the currency flow
      // that propagates the new currency to the linked deal.
      const changedFields: Partial<ContractUpdatePayload> = {
        ...(propagatesCurrencyToDeal ? { dealId: contract.dealId } : {}),
        [field]: finalValue,
        ...normalizedAdditionalFields
      };

      const writeId = ++latestWriteIdRef.current;
      pendingWriteCountRef.current += 1;

      const writtenKeys = [field, ...Object.keys(normalizedAdditionalFields)];
      writtenKeys.forEach((key) => {
        fieldWriteIdsRef.current[key] = writeId;
      });

      // mutateAsync, not mutate: callbacks passed to `mutate` fire only for the
      // most recent call, because TanStack resubscribes the observer on every
      // one. Two overlapping edits therefore left the earlier write's
      // bookkeeping undone — pendingWriteCountRef never decremented, so the
      // dropped-snapshot refetch stayed blocked for the rest of the page's
      // life, and a failing earlier write never restored its own fields.
      void updateContract
        .mutateAsync({
          pathParams: { contractId: contract.id },
          data: changedFields
        })
        .then(
          (response) => {
            pendingWriteCountRef.current -= 1;
            // Record what the server confirmed BEFORE deciding what to paint.
            // These are different questions: ownership decides the visible
            // state, recency of confirmation decides the rollback baseline. A
            // write superseded by a newer one still confirmed its own fields,
            // and if that newer write then fails, this is what it reverts to —
            // without it the field rolled back past a value the server accepted.
            const toConfirm = keysToConfirm(
              writtenKeys,
              confirmedWriteIdsRef.current,
              writeId
            );
            if (toConfirm.length > 0) {
              confirmedContractRef.current = confirmedContractRef.current
                ? applyFields(confirmedContractRef.current, response, toConfirm)
                : response;
              toConfirm.forEach((key) => {
                confirmedWriteIdsRef.current[key] = writeId;
              });
            }

            // Both halves, and the count is the half this used to be missing:
            // being the newest write dispatched says nothing about an OLDER one
            // still in flight. Edit arr, then edit mrr; mrr resolves first and
            // its response still carries the server's pre-arr value, so taking
            // it whole painted the stale arr over the user's own edit, which
            // then reappeared when arr landed. pendingWriteCountRef was already
            // decremented above, so zero here means nothing else is out.
            // Server-recomputed fields still arrive: the invalidation-driven
            // fetch is deferred while writes are pending and
            // refetchIfSnapshotWasDropped runs it once the last one settles.
            if (
              writeId === latestWriteIdRef.current &&
              pendingWriteCountRef.current === 0
            ) {
              // The whole response is authoritative — including fields the
              // server recomputed off this edit but we never sent.
              confirmedContractRef.current = response;
              setContract(response);
            } else {
              // Something else is still unsettled — a newer edit dispatched
              // after this one, or an older one that has not come back yet.
              // Taking the whole response would undo the newer edit, or paint
              // the server's pre-edit value over the older one; dropping the
              // response outright — what this did before — threw away this
              // write's own confirmed fields, which no later response resends.
              // Take exactly the fields this write still owns.
              const ownedKeys = keysOwnedBy(
                writtenKeys,
                fieldWriteIdsRef.current,
                writeId
              );
              if (ownedKeys.length > 0) {
                setContract((current) =>
                  current ? applyFields(current, response, ownedKeys) : current
                );
              }
            }
            // The deal's currency changed on the server either way, so its
            // cache is stale whether or not a newer contract write is pending.
            if (field === "currency" && response.dealId) {
              queryClient.invalidateQueries({
                queryKey: [
                  "deals",
                  { pathParams: { dealsIds: response.dealId } }
                ]
              });
            }
            refetchIfSnapshotWasDropped();
          },
          () => {
            pendingWriteCountRef.current -= 1;
            // Revert this write's own fields rather than the whole contract, and
            // only those a newer write has not since claimed. Reverting wholesale
            // would discard a newer edit to other fields; gating the whole revert
            // on recency instead left the failed value on screen with nothing to
            // correct it, since neither handler refetches.
            const ownedKeys = keysOwnedBy(
              writtenKeys,
              fieldWriteIdsRef.current,
              writeId
            );
            if (ownedKeys.length > 0) {
              // Restore from the last confirmed contract rather than the state
              // this write happened to overwrite — that state can still hold an
              // earlier failed write's unconfirmed value.
              const restoreFrom = confirmedContractRef.current ?? previous;
              setContract((current) =>
                current ? applyFields(current, restoreFrom, ownedKeys) : current
              );
            }
            toast.error(t("contracts.detailedView.updateError"));
            refetchIfSnapshotWasDropped();
          }
        );
    },
    [
      contract,
      updateContract,
      updateDealCurrency,
      t,
      queryClient,
      refetchIfSnapshotWasDropped
    ]
  );

  const switchToOnboardingTab = useCallback(
    () => changeTab(ContractTab.ONBOARDING),
    [changeTab]
  );
  const onboarding = useContractOnboarding(
    contract,
    onUpdateField,
    switchToOnboardingTab
  );

  const handleDelete = useCallback(() => {
    if (!contract) return;
    deleteContract.mutate(
      { pathParams: { contractId: contract.id } },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          navigate("/contracts");
        },
        onError: () => {
          toast.error(t("contracts.detailedView.deleteError"));
        }
      }
    );
  }, [contract, deleteContract, navigate, t]);

  const tabIcons: Record<string, JSX.Element> = useMemo(
    () => ({
      [ContractTab.ONBOARDING]:
        contract?.onboardingCompletedAt != null ? <Check /> : <Onboarding />,
      [ContractTab.RETENTION]: <Workflow />
    }),
    [contract?.onboardingCompletedAt]
  );

  const tabOptions: Record<string, string> = useMemo(
    () => ({
      [ContractTab.OVERVIEW]: t("contracts.detailedView.tabs.overview"),
      [ContractTab.TERMS]: t("contracts.detailedView.tabs.termsAndServices"),
      [ContractTab.ACTIVITIES]: t("contracts.detailedView.tabs.activities"),
      [ContractTab.PEOPLE]: t("contracts.detailedView.tabs.people"),
      ...(onboardingEnabled
        ? {
            [ContractTab.ONBOARDING]: t(
              "contracts.detailedView.tabs.onboarding"
            )
          }
        : {}),
      [ContractTab.RETENTION]: t(
        "contracts.detailedView.tabs.retentionWorkflows"
      ),
      ...(showAdditionalInfoTab
        ? {
            [ContractTab.ADDITIONAL_INFO]: t(
              "contracts.detailedView.tabs.additionalInfo"
            )
          }
        : {})
    }),
    [t, onboardingEnabled, showAdditionalInfoTab]
  );

  const onUpdateAdditionalField = useCallback(
    (
      field: string,
      newValue: string | number,
      additionalFields?: Record<string, string | number>
    ) => {
      onUpdateField(field, newValue, additionalFields);
    },
    [onUpdateField]
  );

  if (isError) {
    return <NotFoundPage data-test="contract-detailed-view-404" />;
  }

  if (isLoading || !contract) {
    return (
      <div className="flex items-center justify-center h-full w-full min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case ContractTab.OVERVIEW:
        return (
          <ContractOverview
            contract={contract}
            onUpdateField={onUpdateField}
            toggleTasksDrawer={toggleTasksDrawer}
          />
        );
      case ContractTab.TERMS:
        return (
          <ContractTermsAndServicesTab
            contract={contract}
            onUpdateField={onUpdateField}
          />
        );
      case ContractTab.ACTIVITIES:
        return (
          <Activities
            pageName="contracts"
            entityData={contract}
            entityType={EntityType.CONTRACT}
            isTasksDrawerOpen={isTasksDrawerOpen}
          />
        );
      case ContractTab.PEOPLE:
        return (
          <ContractPeopleTab
            contractId={contract.id}
            companyId={contract.companyId}
          />
        );
      case ContractTab.ONBOARDING:
        return <OnboardingTab contract={contract} state={onboarding} />;
      case ContractTab.RETENTION:
        return (
          <InTheMakingTab
            illustration={<EmptyStateIllustrationRetentionWorkflow />}
            title={t("contracts.detailedView.retentionWorkflows.title")}
            description={t(
              "contracts.detailedView.retentionWorkflows.description"
            )}
            dataTest="contract-detailed-view-retention-workflows"
          />
        );
      case ContractTab.ADDITIONAL_INFO:
        return (
          <AdditionalInfoTab
            entityData={contract}
            entityType={EntityType.CONTRACT}
            onUpdateField={onUpdateAdditionalField}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col h-full",
        isTasksDrawerOpen && "max-h-[var(--app-content-h)]"
      )}
      data-test="contract-detailed-view"
    >
      <div
        className="flex flex-col top-0 bg-surface-elevation0 z-10 sticky w-full"
        data-test="contract-detailed-view-header"
      >
        <div className="w-full flex justify-between gap-2 p-4 pb-2">
          {/* `min-w-0` so a long contract name shrinks this group instead of
              growing the row: without it the name sets the floor and the
              actions beside it wrap to a second line. */}
          <div className="flex gap-1 items-center min-w-0">
            <div className="flex gap-[10px] items-center shrink-0">
              <LogoIconContainer
                icon={<ContractsIcon />}
                index={-1}
                className="size-8 flex-shrink-0"
                iconSize="5"
              />
              <Link
                to="/contracts"
                data-test="contract-detailed-view-breadcrumb-link"
              >
                <Text variant="lgSubtitleH3" className="p-1">
                  {t("pages.contracts")}
                </Text>
              </Link>
            </div>
            <IconContainer color="secondary" size="5" className="shrink-0">
              <ChevronRight />
            </IconContainer>
            <SingleInput
              fieldName="name"
              initialValue={contract.name}
              onSave={(newValue: string) => onUpdateField("name", newValue)}
              textVariant="lgSubtitleH1"
              inputVariant="lgSubtitleH1"
              className="pl-0 min-w-0"
              width="fit-content"
              truncate
              placeholder={t("contracts.detailedView.emptyName")}
              divClassName="py-0 px-2"
              data-test="contract-detailed-view-name-input"
            />
            {onboardingEnabled && !onboarding.isCompleted && (
              <OnboardingBadge
                onboardingStartDate={contract.onboardingStartDate}
                activeOnboardingStage={onboarding.activeStage ?? undefined}
                activeOnboardingStageColor={onboarding.activeStageColor}
                hasOnboardingStages={onboarding.hasStages}
              />
            )}
          </div>
          <div className="flex gap-2 items-center shrink-0">
            {onboardingEnabled &&
              !onboarding.isActive &&
              !onboarding.isScheduled &&
              !onboarding.isCompleted && (
                <Button
                  variant="secondary"
                  onClick={onboarding.handleStart}
                  data-test="contract-detailed-view-start-onboarding-button"
                >
                  {t("contracts.detailedView.onboarding.startOnboarding")}
                </Button>
              )}
            {onboardingEnabled && onboarding.isActiveWithNoStages && (
              <Button
                variant="secondary"
                onClick={onboarding.handleComplete}
                data-test="contract-detailed-view-complete-onboarding-button"
                className="h-9"
              >
                {t("contracts.detailedView.onboarding.completeOnboarding")}
              </Button>
            )}
            {onboardingEnabled &&
            onboarding.isActive &&
            onboarding.hasStages ? (
              <OnboardingStageSelection
                stages={onboarding.stages}
                currentStageId={onboarding.currentStageId}
                onStageSelection={onboarding.handleSelectStage}
                onComplete={onboarding.handleComplete}
              />
            ) : null}
            <OptionsDropdownMenu
              tPrefix="contracts.detailedView.options"
              triggerDataTest="contract-detailed-view-options-menu"
              iconSize="lg"
              disableCloseAutoFocus
              options={{
                copyLink: () => handleCopyLink(),
                ...(showDuplicateContract && {
                  duplicateContract: () => navigate("duplicate")
                }),
                contractLog: () => navigate("log"),
                delete: {
                  action: () => setIsDeleteModalOpen(true),
                  variant: "destructive"
                }
              }}
              topOptionsTitle={
                onboardingEnabled &&
                onboarding.isScheduled &&
                onboarding.parsedStartDate
                  ? t("contracts.detailedView.onboarding.startsAt", {
                      date: simpleDate(
                        onboarding.parsedStartDate,
                        "short",
                        false,
                        true
                      )
                    })
                  : undefined
              }
              topOptions={buildOnboardingTopOptions(
                onboarding,
                onboardingEnabled
              )}
            />
          </div>
        </div>
        <ContractTopFields contract={contract} onUpdateField={onUpdateField} />
        <div className="flex w-full pr-4 items-center relative border-b-[1px] border-widget">
          <div className="flex-grow">
            <LayoutTabs
              options={tabOptions}
              icons={tabIcons}
              defaultValue={activeTab}
              valueSelected={(value) => changeTab(value as ContractTab)}
              className="border-none"
            />
          </div>
          <IconButton
            variant="iconNoBg"
            size="lg"
            onClick={toggleTasksDrawer}
            className={cn(isTasksDrawerOpen && "text-primary")}
            iconColor={isTasksDrawerOpen ? "primary" : "secondary"}
            data-test="contract-detailed-view-tasks-button"
          >
            <Tasks />
          </IconButton>
        </div>
      </div>
      <div className="flex w-full flex-1 min-h-0">
        <div
          className={cn(
            "w-full p-6 min-h-0",
            activeTab === ContractTab.PEOPLE.toString() && "p-0",
            // bounding the view would otherwise clip the onboarding tab's own overflow away
            activeTab === ContractTab.ONBOARDING.toString() &&
              !isTasksDrawerOpen
              ? "overflow-hidden"
              : "overflow-y-auto"
          )}
          data-test="contract-detailed-view-tab-content"
        >
          {renderTabContent()}
        </div>
        <TasksDrawer
          isOpen={isTasksDrawerOpen}
          toggleDrawer={toggleTasksDrawer}
          entityData={contract}
          entityType={EntityType.CONTRACT}
        />
      </div>
      <DeleteEntityModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        entity={EntityType.CONTRACT}
        entityName={contract.name}
        handleDelete={handleDelete}
      />
      <OnboardingGuardrailsModal
        isOpen={onboarding.guardrailsBlockedBy.length > 0}
        onClose={() => onboarding.setGuardrailsBlockedBy([])}
        blockedBy={onboarding.guardrailsBlockedBy}
        onSelectWorkflow={onboarding.handleOpenBlockedWorkflow}
      />
    </div>
  );
};

export default ContractDetailedView;
